import React, { useState } from 'react';
import dayjs from 'dayjs';
import ExcelJS from 'exceljs';
import * as FileSaver from 'file-saver';
import { Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import useCommonStore from 'stores/useCommon';
import { toLocalTime } from 'utils';
import API from 'services/api/index';
import Logo from 'assets/images/logo.png';
import {
  BookingRemindType,
  CategoriesBookingReminderType,
  ParamsGetBookingReminds
} from 'services/api/type/booking.type';
import { REMIND_CATEGORIES_OPTIONS } from 'components/modal/booking-remind/remind-categories-option';

interface Props {
  fileName: string;
  filterOptions: ParamsGetBookingReminds;
}

const ExportBookingReminds = ({ fileName, filterOptions }: Props) => {
  const { t } = useTranslation();
  const { currentBranch } = useCommonStore();
  const [isLoadingExport, setIsLoadingExport] = useState(false);

  const getAllData = async () => {
    const { data } = await API.booking.getBookingReminds(filterOptions, 0, 1000000);
    return data as BookingRemindType[];
  };

  const exportToExcel = async () => {
    const fileType =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    try {
      setIsLoadingExport(true);
      const result = await getAllData();
      const dataToExport = result.map((item, index) => {
        const isOther = item.category === CategoriesBookingReminderType.OTHER;
        const categoryDisplay =
          item.category_display && isOther
            ? item.category_display
            : t(
                REMIND_CATEGORIES_OPTIONS.find(opt => opt.value === (item.category as string))
                  ?.label || ''
              );

        return {
          stt: index + 1,
          booking_id: item.booking_id,
          booking_line_id: item.booking_line_id,
          category: categoryDisplay || '',
          room_no: item.room_no?.room_no
            ? `${item.room_type_name} (${item.room_no.room_no})`
            : `${item.room_type_name} (N/A)`,
          main_guest: item.main_guest,
          gender:
            item.gender === 'male'
              ? t('common.customer.male')
              : item.gender === 'female'
                ? t('common.customer.female')
                : '-',
          travel_agency: item.travel_agency || '',
          note: item.note || '',
          date: item.date ? toLocalTime(item.date, 'DD/MM/YYYY') : '-',
          created_date: item.created_date
            ? toLocalTime(item.created_date, 'DD/MM/YYYY HH:mm')
            : '-',
          user_name: item.user_name || '',
          check_in: item.check_in ? toLocalTime(item.check_in, 'DD/MM/YYYY HH:mm') : '-',
          check_out: item.check_out ? toLocalTime(item.check_out, 'DD/MM/YYYY HH:mm') : '-'
        };
      });

      // Sheet name
      const workbook = new ExcelJS.Workbook();
      const sheetName = `BookingReminds_${dayjs(filterOptions.remind_from).format('DD-MM-YYYY')}_${dayjs(filterOptions.remind_to).format('DD-MM-YYYY')}`;
      const sheet = workbook.addWorksheet(sheetName, {
        views: [
          {
            showGridLines: false
          }
        ]
      });

      // Title
      sheet.mergeCells('A1:N1');
      sheet.getCell('A1').value = currentBranch?.name?.toUpperCase() || '';
      sheet.getCell('A1').font = { bold: true, size: 14 };
      sheet.getCell('A1').alignment = { horizontal: 'center' };

      // Address
      sheet.mergeCells('A2:N2');
      sheet.getCell('A2').value =
        `${t('subHeader.sheetHeader.address')}: ${currentBranch?.address || ''}`;
      sheet.getCell('A2').font = { size: 10 };
      sheet.getCell('A2').alignment = { horizontal: 'center' };

      // Support
      sheet.mergeCells('A3:N3');
      sheet.getCell('A3').value = currentBranch?.phone_contact
        ? `${t('subHeader.sheetHeader.phoneNo')}: ${currentBranch.phone_contact}`
        : '';
      sheet.getCell('A3').font = { size: 10 };
      sheet.getCell('A3').alignment = { horizontal: 'center' };

      // Support subtitle
      sheet.mergeCells('A4:N4');
      sheet.getCell('A4').value = t('bookingReminds.title').toUpperCase();
      sheet.getCell('A4').font = { bold: true, size: 12 };
      sheet.getCell('A4').alignment = { horizontal: 'center' };

      // Filter value
      sheet.mergeCells('B6:C6');
      sheet.mergeCells('E6:F6');

      sheet.getCell('A6').value = `${t('common.time.fromDate')}:`;
      sheet.getCell('A6').font = { bold: true };
      sheet.getCell('B6').value = dayjs(filterOptions.remind_from).format('DD-MM-YYYY');

      sheet.getCell('D6').value = `${t('common.time.toDate')}:`;
      sheet.getCell('D6').font = { bold: true };
      sheet.getCell('E6').value = dayjs(filterOptions.remind_to).format('DD-MM-YYYY');

      if (filterOptions.booking_line_id) {
        sheet.mergeCells('H6:I6');
        sheet.getCell('G6').value = `${t('bookingReminds.bookingLineId')}:`;
        sheet.getCell('G6').font = { bold: true };
        sheet.getCell('H6').value = filterOptions.booking_line_id;
        sheet.getCell('H6').alignment = { horizontal: 'left' };
      }

      if (filterOptions.category !== undefined) {
        sheet.mergeCells('K6:M6');
        sheet.getCell('J6').value = `${t('common.categoties.category')}:`;
        sheet.getCell('J6').font = { bold: true };
        sheet.getCell('K6').value = t(
          REMIND_CATEGORIES_OPTIONS.find(item => item.value === (filterOptions.category as string))
            ?.label || ''
        );
      }

      // Apply borders to all cells in row 6 (columns A to N)
      for (let col = 1; col <= 14; col++) {
        const cell = sheet.getRow(6).getCell(col);
        cell.border = {
          top: { style: 'medium', color: { argb: '000000' } },
          bottom: { style: 'medium', color: { argb: '000000' } }
        };
      }
      sheet.getRow(6).height = 25;

      // Mark column
      sheet.getRow(7).values = [
        '[1]',
        '[2]',
        '[3]',
        '[4]',
        '[5]',
        '[6]',
        '[7]',
        '[8]',
        '[9]',
        '[10]',
        '[11]',
        '[12]',
        '[13]',
        '[14]'
      ];

      // Header columns
      sheet.getRow(8).values = [
        t('common.no'),
        t('bookingReminds.bookingId'),
        t('bookingReminds.bookingLineId'),
        t('common.categoties.category'),
        t('common.bookingRemind.roomNo'),
        t('common.customer.mainGuest'),
        t('common.customer.gender'),
        'TA',
        t('bookingReminds.remindContent'),
        t('bookingReminds.remindDate'),
        t('bookingReminds.createdDate'),
        t('bookingReminds.creator'),
        t('common.time.checkIn'),
        t('common.time.checkOut')
      ];

      sheet.getCell('A7').alignment = { horizontal: 'center' };
      sheet.getCell('A8').alignment = { horizontal: 'center' };
      // Apply borders to all header cells in row 8
      for (let col = 1; col <= 14; col++) {
        const cell = sheet.getRow(8).getCell(col);
        cell.border = {
          bottom: { style: 'medium', color: { argb: '000000' } }
        };
        cell.font = { bold: true };
      }

      // Data
      sheet.columns = [
        {
          key: 'stt',
          width: 10,
          style: { alignment: { horizontal: 'center' } }
        },
        { key: 'booking_id', width: 12, style: { alignment: { horizontal: 'center' } } },
        { key: 'booking_line_id', width: 12, style: { alignment: { horizontal: 'center' } } },
        { key: 'category', width: 15, style: { alignment: { horizontal: 'center' } } },
        { key: 'room_no', width: 12 },
        { key: 'main_guest', width: 25 },
        { key: 'gender', width: 12 },
        { key: 'travel_agency', width: 20 },
        { key: 'note', width: 50 },
        { key: 'date', width: 15 },
        { key: 'created_date', width: 18 },
        { key: 'user_name', width: 20 },
        { key: 'check_in', width: 18 },
        { key: 'check_out', width: 18 }
      ];

      dataToExport.forEach(item => {
        sheet.addRow(item);
      });

      // Apply borders to all table cells
      const lastRow = 8 + dataToExport.length;
      for (let row = 8; row <= lastRow; row++) {
        for (let col = 1; col <= 14; col++) {
          const cell = sheet.getRow(row).getCell(col);
          cell.border = {
            top: { style: 'thin', color: { argb: '#000000' } },
            left: { style: 'thin', color: { argb: '#000000' } },
            bottom: { style: 'thin', color: { argb: '#000000' } },
            right: { style: 'thin', color: { argb: '#000000' } }
          };
        }
      }

      const imageId2 = workbook.addImage({
        base64: Logo,
        extension: 'png'
      });
      sheet.addImage(imageId2, {
        tl: { col: 1, row: 1 },
        ext: { width: 71, height: 57 }
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const data = new Blob([buffer], { type: fileType });
      FileSaver.saveAs(data, fileName + fileExtension);
    } catch (e) {
      console.log('ERROR', e);
    } finally {
      setIsLoadingExport(false);
    }
  };

  return (
    <Button icon={<DownloadOutlined />} loading={isLoadingExport} onClick={exportToExcel}>
      {t('common.actions.exportData')}
    </Button>
  );
};

export default ExportBookingReminds;
